import React, { FC, useCallback, useState } from 'react';
import { Button, Modal, Row } from 'antd';
import { CloudDownloadOutlined, PlusCircleFilled } from '@ant-design/icons';
import ImportGridModal from './ImportGridModal';

interface OWFlatfileButtonProps {
	type: string;
	fields: any[];
	onDataLoad: (result: { validData: any[] }) => Promise<any>;
	recordHook?: (record: any) => any;
	onCancel?: () => void;
	renderElement: (openPortal: () => void) => React.ReactNode;
	validateRecordsOnSubmit?: (data: any[]) => string | undefined | null | false;
	// When provided, opens a pre-modal asking the user to download a sample CSV
	// before proceeding to the import grid. Mirrors the legacy
	// `UploadWithSampleModal` UX so feature parity is preserved as we migrate
	// off Flatfile.
	onDownloadSample?: () => void;
	sampleModalTitle?: string;
	sampleModalText?: string;
	sampleButtonText?: string;
}

const OWFlatfileButton: FC<OWFlatfileButtonProps> = ({
	type,
	fields,
	onDataLoad,
	recordHook,
	onCancel,
	renderElement,
	validateRecordsOnSubmit,
	onDownloadSample,
	sampleModalTitle,
	sampleModalText,
	sampleButtonText,
}) => {
	const [sampleModalVisible, setSampleModalVisible] = useState(false);
	const [importModalVisible, setImportModalVisible] = useState(false);

	const openPortal = useCallback(() => {
		if (onDownloadSample) {
			setSampleModalVisible(true);
		} else {
			setImportModalVisible(true);
		}
	}, [onDownloadSample]);

	const handleProceedToUpload = useCallback(() => {
		setSampleModalVisible(false);
		setImportModalVisible(true);
	}, []);

	const handleSampleCancel = useCallback(() => {
		setSampleModalVisible(false);
		if (onCancel) onCancel();
	}, [onCancel]);

	const handleImportCancel = useCallback(() => {
		setImportModalVisible(false);
		if (onCancel) onCancel();
	}, [onCancel]);

	const handleImportComplete = useCallback(() => {
		setImportModalVisible(false);
	}, []);

	return (
		<>
			{renderElement(openPortal)}
			{sampleModalVisible && (
				<Modal
					visible={sampleModalVisible}
					title={sampleModalTitle || `Import ${type}`}
					closable
					onCancel={handleSampleCancel}
					forceRender
					footer={null}
				>
					<Row>{sampleModalText || 'Do you want to download a sample file?'}</Row>
					<Row>
						<Button
							type="primary"
							className="mt-6 w-full"
							onClick={onDownloadSample}
							icon={<CloudDownloadOutlined translate="" />}
						>
							{sampleButtonText || 'Download Sample File'}
						</Button>
					</Row>
					<Row>
						<Button
							icon={<PlusCircleFilled translate="" />}
							className="mt-3 w-full"
							onClick={handleProceedToUpload}
						>
							Proceed to Upload
						</Button>
					</Row>
				</Modal>
			)}
			{importModalVisible && (
				<ImportGridModal
					visible={importModalVisible}
					title={`Import ${type}`}
					fields={fields}
					recordHook={recordHook}
					validateRecordsOnSubmit={validateRecordsOnSubmit}
					onDataLoad={onDataLoad}
					onCancel={handleImportCancel}
					onComplete={handleImportComplete}
				/>
			)}
		</>
	);
};

export default OWFlatfileButton;
